import React, {useEffect, useState} from 'react';
import {Chip, CircularProgress, Divider, IconButton, Paper, Stack, Tooltip, Typography,} from '@mui/material';
import {useNavigate, useParams} from 'react-router';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import CircleIcon from '@mui/icons-material/Circle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useFeeds from "../hooks/useFeeds";
import HeaderOfListOfFeeds from "./HeaderOfListOfFeeds";

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {'Authorization': `Bearer ${t}`} : {}; }

const FeedCard = ({feed, isActive, onClick, onDeleted}) => {
    const desc = feed.description || '';
    const snippet = desc.length > 80 ? desc.slice(0, 77) + '…' : desc;
    const [hovered, setHovered] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async (e) => {
        e.stopPropagation();
        setDeleting(true);
        try {
            await fetch(`${_awpBase}/api/feeds/${encodeURIComponent(feed.id)}`, {method: 'DELETE', headers: _authH()});
            if (typeof onDeleted === 'function') onDeleted();
        } catch (err) {
            console.error('Failed to delete feed', err);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Stack
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
            sx={{
                px: 1.5,
                py: 1.25,
                cursor: 'pointer',
                opacity: deleting ? 0.4 : 1,
                bgcolor: isActive ? '#e8f0fe' : 'white',
                borderLeft: isActive ? '3px solid #1976d2' : '3px solid transparent',
                borderBottom: '1px solid #f0f0f0',
                '&:hover': {bgcolor: isActive ? '#e8f0fe' : '#f5f5f5'},
                transition: 'background-color 0.12s ease',
            }}
        >
            <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                <RssFeedIcon sx={{fontSize: 16, color: isActive ? '#1976d2' : '#9e9e9e', mt: 0.3, flexShrink: 0}}/>

                <Stack flexGrow={1} minWidth={0} spacing={0.4}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.5}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: isActive ? 700 : 500,
                                lineHeight: 1.35,
                                color: isActive ? 'primary.main' : 'text.primary',
                                wordBreak: 'break-word',
                                flexGrow: 1,
                            }}
                        >
                            {feed.title || 'Untitled feed'}
                        </Typography>

                        <Tooltip title="Delete feed" placement="top">
                            <IconButton
                                size="small"
                                onClick={handleDelete}
                                disabled={deleting}
                                sx={{
                                    flexShrink: 0,
                                    opacity: hovered ? 1 : 0,
                                    transition: 'opacity 0.15s ease',
                                    p: 0.25,
                                    mt: -0.25,
                                    color: 'text.secondary',
                                    '&:hover': {color: 'error.main'},
                                }}
                            >
                                <DeleteOutlineIcon sx={{fontSize: 15}}/>
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {snippet && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{lineHeight: 1.4, display: 'block', wordBreak: 'break-word'}}
                        >
                            {snippet}
                        </Typography>
                    )}

                    <Chip
                        icon={<CircleIcon sx={{fontSize: '7px !important', color: '#4caf50 !important'}}/>}
                        label="FINTEL"
                        size="small"
                        sx={{
                            alignSelf: 'flex-start',
                            height: 16,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            bgcolor: '#e8f5e9',
                            color: '#2e7d32',
                            borderRadius: '4px',
                            '& .MuiChip-label': {px: 0.6},
                            mt: 0.25,
                        }}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
};

const ListOfFeeds = ({refreshKey = 0, onFeedCreated}) => {
    const {feeds, loading, error} = useFeeds(refreshKey);
    const {feedId} = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (feedId || loading || error || !feeds || feeds.length === 0) return;
        try { navigate(feeds[0].id, {replace: true}); }
        catch (e) { console.error('Failed to auto-select first feed:', e); }
    }, [feedId, loading, error, feeds, navigate]);

    if (loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{p: 2}}>
                <CircularProgress size={24}/>
            </Stack>
        );
    }

    if (error) {
        return (
            <Paper sx={{p: 2}}>
                <Typography color="error">Error loading feeds.</Typography>
                <Typography variant="caption">{error.message}</Typography>
            </Paper>
        );
    }

    return (
        <Stack>
            <HeaderOfListOfFeeds onFeedCreated={onFeedCreated}/>
            <Divider/>

            {feeds.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                    No feeds yet. Create one above.
                </Typography>
            )}

            {feeds.map(feed => (
                <FeedCard
                    key={feed.id}
                    feed={feed}
                    isActive={feedId === feed.id}
                    onClick={() => navigate(feed.id)}
                    onDeleted={() => {
                        if (typeof onFeedCreated === 'function') onFeedCreated();
                        if (feedId === feed.id) navigate('/feed');
                    }}
                />
            ))}
        </Stack>
    );
};

export default ListOfFeeds;
